import { ApexOptions } from 'apexcharts';
import { pick } from 'lodash';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import { useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useSearchParams } from 'react-router-dom';
import BalanceBarChart from '@/modules/common/components/BalanceBarChart.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useGetPaymentElectionQuery } from '@/modules/issuer-dashboard/queries/charts.ts';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { InfoCard, Select, Spinner } from 'ui';
import { goodFormatNumber } from 'ui/utils';

const formatInvestors = (total: number) => {
  return `${goodFormatNumber(total)} investors`;
};

const investorsChartOptions: ApexOptions = {
  tooltip: {
    custom: ({ seriesIndex, series, w }) => {
      const label = capitalize(lowerCase(w.config.series[seriesIndex]?.name || ''));
      return renderToString(
        <div className="p-2">
          {label}: {series[seriesIndex]} investors
        </div>,
      );
    },
  },
};

const PaymentElection = () => {
  const { data: dataPaymentElection, isLoading } = useGetPaymentElectionQuery();
  const [params] = useSearchParams();
  const currentRegisterId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');
  const { data: classesData, isLoading: isLoadingClasses } = useIssuerRegisterClassListQuery(
    {},
    currentRegisterId,
  );
  const [registerClassFilter, setRegisterClassFilter] = useState<number | null>();

  const classesOptions = useMemo(
    () => [
      ...(classesData?.registers || []).map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      })),
    ],
    [classesData?.registers],
  );

  return (
    <InfoCard
      title="Holder Payment Election"
      headerAction={
        <div>
          <Select
            label=""
            isLoading={isLoadingClasses}
            placeholder="Classes"
            className="min-w-[190px] w-full"
            onChange={setRegisterClassFilter}
            value={registerClassFilter || classesOptions?.[0]?.value}
            options={classesOptions}
            withBorder={true}
          />
        </div>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : dataPaymentElection?.data?.find(
          item => item.register_class_id === (registerClassFilter || classesOptions?.[0]?.value),
        ) ? (
        <BalanceBarChart
          data={
            pick(
              dataPaymentElection?.data?.find(
                item =>
                  item.register_class_id === (registerClassFilter || classesOptions?.[0]?.value),
              ),
              ['drp', 'direct_credit'],
            ) as any
          }
          formatTotal={formatInvestors}
          options={investorsChartOptions}
        />
      ) : (
        <span className="text-gray-500 text-sm w-full flex justify-center">
          There are no holders or transactions in this class
        </span>
      )}
    </InfoCard>
  );
};

export default PaymentElection;
